/**
 * TanStack Query hooks for IdP Connection management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { isApiError } from '@/lib/api-client'
import {
  fetchIdPConnections,
  fetchIdPConnection,
  createIdPConnection,
  updateIdPConnection,
  deleteIdPConnection,
  testIdPConnection,
  triggerIdPSync,
  fetchIdPSyncHistory,
} from '@/lib/uas-api'
import { syncRunKeys } from '@/hooks/use-sync-runs'
import type {
  CreateIdPConnectionRequest,
  UpdateIdPConnectionRequest,
} from '@/types/uas'

export const idpConnectionKeys = {
  all: ['uas', 'idp-connections'] as const,
  lists: () => [...idpConnectionKeys.all, 'list'] as const,
  details: () => [...idpConnectionKeys.all, 'detail'] as const,
  detail: (id: string) => [...idpConnectionKeys.details(), id] as const,
  syncHistory: (id: string) => [...idpConnectionKeys.detail(id), 'sync-history'] as const,
}

export function useIdPConnections() {
  return useQuery({
    queryKey: idpConnectionKeys.lists(),
    queryFn: () => fetchIdPConnections(),
    staleTime: 30 * 1000,
  })
}

export function useIdPConnection(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? idpConnectionKeys.detail(id)
      : ['uas', 'idp-connections', 'detail', 'disabled'],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Connection ID is required'))
      return fetchIdPConnection(id)
    },
    enabled: !!id,
    retry: false,
    staleTime: 10 * 1000,
  })
}

export function useIdPSyncHistory(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? idpConnectionKeys.syncHistory(id)
      : ['uas', 'idp-connections', 'sync-history', 'disabled'],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Connection ID is required'))
      return fetchIdPSyncHistory(id)
    },
    enabled: !!id,
    staleTime: 10 * 1000,
  })
}

export function useCreateIdPConnection() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: CreateIdPConnectionRequest) => createIdPConnection(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: idpConnectionKeys.lists() })
      toast.success('IdP connection created successfully')
      navigate(`/identity/connections/${data.id}`)
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to create connection: ${message}`)
    },
  })
}

export function useUpdateIdPConnection(connectionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateIdPConnectionRequest) =>
      updateIdPConnection(connectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: idpConnectionKeys.detail(connectionId),
      })
      queryClient.invalidateQueries({ queryKey: idpConnectionKeys.lists() })
      toast.success('Connection updated successfully')
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to update connection: ${message}`)
    },
  })
}

export function useDeleteIdPConnection() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (id: string) => deleteIdPConnection(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: idpConnectionKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: idpConnectionKeys.lists() })
      navigate('/identity/connections')
      toast.success('Connection deleted successfully')
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to delete connection: ${message}`)
    },
  })
}

export function useTestIdPConnection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => testIdPConnection(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: idpConnectionKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: idpConnectionKeys.lists() })
      if (result.success) {
        toast.success(`Connection successful (${result.latencyMs}ms)`)
      } else {
        toast.error(`Connection failed: ${result.error ?? 'Unknown error'}`)
      }
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to test connection: ${message}`)
    },
  })
}

export function useTriggerSync() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (connectionId: string) => triggerIdPSync(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({
        queryKey: idpConnectionKeys.detail(connectionId),
      })
      queryClient.invalidateQueries({
        queryKey: idpConnectionKeys.syncHistory(connectionId),
      })
      queryClient.invalidateQueries({ queryKey: syncRunKeys.lists() })
      toast.success('Sync triggered successfully')
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to trigger sync: ${message}`)
    },
  })
}
